import {
  faCopy,
  faCut,
  faObjectGroup,
  faTrashAlt,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { capitalize } from "lodash-es";
import { observer } from "mobx-react-lite";
import React from "react";

import { useSelectedNodesLockedByOtherUser } from "src/authoringMultiplayerLock/useSelectedNodesLockedByOtherUser";
import { toastActions } from "src/design-system/Toast/utils";
import { MultiSelectOptionsIcon } from "src/flowGraph/multiSelectOptions/MultiSelectOptionsIcon";
import { getCopySuccessMessage } from "src/flowGraph/nodeControls/NodeControls";
import { useCanAuthoringEditFlowVersion } from "src/hooks/useCanAuthoringEditFlowVersion";
import { getCreatableNodes } from "src/nodeAdding/PasteNodesCard";
import { AuthorPageParams } from "src/router/urls";
import { useGraphStore } from "src/store/StoreProvider";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const InvalidCopySelectionReasonMessages = {
  NotEnoughNodes: "less than 2 nodes",
  NoInputOutput: "selection with input / output nodes",
  NotConnected: "disconnected nodes",
  NotAllSplitNodeChildren: "incomplete split nodes",
} as const;
export const InvalidGroupReasonMessages = {
  ...InvalidCopySelectionReasonMessages,
  NoGroups: "selection with Node groups or nodes that are already in a group",
} as const;

export type InvalidCopySelectionReason =
  keyof typeof InvalidCopySelectionReasonMessages;
export type InvalidGroupReason = keyof typeof InvalidGroupReasonMessages;

export const MultiSelectOptions: React.FC = observer(() => {
  const {
    multiSelectionSelectedNodes,
    isValidGroup,
    setIsCreateGroupModalVisible,
    setSelectedNode,
    copySelectedNodesToClipboard,
    isValidCopyDeleteSelection,
    setIsDeleteNodesModalVisible,
  } = useGraphStore();
  const selectedNodesLockedByOtherUser = useSelectedNodesLockedByOtherUser();
  const { wsId } = useParamsDecode<AuthorPageParams>();
  const canModifyGraph = useCanAuthoringEditFlowVersion();

  if (multiSelectionSelectedNodes === undefined) return <></>;

  const handleCopyNodesToClipboard = async (isCutting?: boolean) => {
    const clipboard = await copySelectedNodesToClipboard(wsId, isCutting);
    if (clipboard) {
      toastActions.success({
        title: getCopySuccessMessage(clipboard, isCutting),
      });
    }
  };

  const getTooltipTitle = (action: "group" | "cut" | "copy" | "delete") => {
    if (selectedNodesLockedByOtherUser) {
      return `Cannot ${action} locked Nodes`;
    }

    if (action === "group") {
      if (isValidGroup.valid) {
        return "Group Nodes";
      }

      return `Cannot group ${InvalidGroupReasonMessages[isValidGroup.reason]}`;
    }

    if (isValidCopyDeleteSelection.valid) {
      return `${capitalize(action)} nodes`;
    }

    return `Cannot ${action} ${InvalidCopySelectionReasonMessages[isValidCopyDeleteSelection.reason]}`;
  };

  return (
    <div
      className="absolute bottom-1 left-0 z-20 mb-6 ml-6"
      data-loc="multi-select-options"
    >
      <div className="relative flex gap-2 rounded-lg bg-white px-3 shadow-base">
        <h3 className="border-r border-gray-200 py-3.5 pr-3 text-gray-600 font-inter-medium-13px">
          {getCreatableNodes(multiSelectionSelectedNodes).length} selected nodes
        </h3>
        {canModifyGraph && (
          <>
            <MultiSelectOptionsIcon
              dataLoc="group-nodes"
              deactivated={
                !isValidGroup.valid || !!selectedNodesLockedByOtherUser
              }
              icon={faObjectGroup}
              tooltipTitle={getTooltipTitle("group")}
              borderRight
              onClick={() => setIsCreateGroupModalVisible(true)}
            />
            <MultiSelectOptionsIcon
              deactivated={
                !isValidCopyDeleteSelection.valid ||
                !!selectedNodesLockedByOtherUser
              }
              icon={faCut}
              tooltipTitle={getTooltipTitle("cut")}
              borderRight
              onClick={() => handleCopyNodesToClipboard(true)}
            />
          </>
        )}
        <MultiSelectOptionsIcon
          dataLoc="copy-nodes"
          deactivated={
            !isValidCopyDeleteSelection.valid ||
            !!selectedNodesLockedByOtherUser
          }
          icon={faCopy}
          tooltipTitle={getTooltipTitle("copy")}
          borderRight
          onClick={() => handleCopyNodesToClipboard(false)}
        />
        {canModifyGraph && (
          <MultiSelectOptionsIcon
            deactivated={
              !isValidCopyDeleteSelection.valid ||
              !!selectedNodesLockedByOtherUser
            }
            icon={faTrashAlt}
            tooltipTitle={getTooltipTitle("delete")}
            borderRight
            onClick={() => {
              setIsDeleteNodesModalVisible(true);
            }}
          />
        )}
        <MultiSelectOptionsIcon
          deactivated={false}
          icon={faXmark}
          tooltipTitle="Deselect all Nodes"
          onClick={() => setSelectedNode(null)}
        />
      </div>
    </div>
  );
});
